import { summarizeActivityChanges } from "@lumika/shared";
import type { ChangeValueResolvers } from "@lumika/shared";

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
}

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return "—";
  }
}

/**
 * `formatDateTime`/clock-time pinned to an IANA zone instead of the
 * admin's browser zone. Appointment times are STORE times — the admin
 * must read the wall clock the customer was promised, whatever zone
 * they browse from.
 */
export function formatDateTimeInZone(
  iso: string | null | undefined,
  timeZone: string,
): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      timeZone,
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    // Unknown zone string — browser-local beats nothing.
    return formatDateTime(iso);
  }
}

export function formatClockTimeInZone(
  iso: string | null | undefined,
  timeZone: string,
): string {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleTimeString(undefined, {
      timeZone,
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

export function humanize(value: string | null | undefined): string {
  if (!value) return "—";
  return value.replace(/_/g, " ");
}

/**
 * Activity bodies sometimes carry a structured diff —
 * `{"changes":{ field:{ before, after } }}` — written when an entity
 * update produced a meaningful change. Rendered raw it's unreadable
 * JSON. Turn it into an explicit plain-English line with the before →
 * after values ("Status: Working → Lost"); return null when the body
 * is not a diff so callers fall back to the normal text.
 *
 * Pass resolvers (userName/channelName) where the caller has lookup
 * maps so ids render as names instead of neutral placeholders.
 */
export function summarizeChangeBody(
  body: string | null | undefined,
  resolvers: ChangeValueResolvers = {},
): string | null {
  return summarizeActivityChanges(body, {
    formatDateTime,
    ...resolvers,
  });
}

/**
 * Verbose relative time: "5 minutes ago" / "in 2 days". Used in
 * the Contact 360 snapshot strip so an operator can scan "last
 * activity" without mentally subtracting timestamps.
 */
export function formatRelativeTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const target = new Date(iso).getTime();
  if (Number.isNaN(target)) return "—";
  const diffMs = target - Date.now();
  const future = diffMs > 0;
  const seconds = Math.round(Math.abs(diffMs) / 1000);
  const minutes = Math.round(seconds / 60);
  const hours = Math.round(minutes / 60);
  const days = Math.round(hours / 24);
  const months = Math.round(days / 30);
  const years = Math.round(days / 365);
  let value: number;
  let unit: string;
  if (seconds < 60) {
    value = seconds;
    unit = "second";
  } else if (minutes < 60) {
    value = minutes;
    unit = "minute";
  } else if (hours < 24) {
    value = hours;
    unit = "hour";
  } else if (days < 30) {
    value = days;
    unit = "day";
  } else if (months < 12) {
    value = months;
    unit = "month";
  } else {
    value = years;
    unit = "year";
  }
  const plural = value === 1 ? unit : `${unit}s`;
  return future ? `in ${value} ${plural}` : `${value} ${plural} ago`;
}

/**
 * Compact inbox-style relative time: "just now" / "5m" / "2h" /
 * "3d" / "Mar 5" / "Mar 5 '24". Used in the inbox lead
 * list where vertical space per row is tight and a full sentence
 * would push the contact name out of the row.
 */
export function formatInboxRelativeTime(iso: string | null | undefined): string {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  const diffMs = Date.now() - date.getTime();
  const diffSec = Math.round(diffMs / 1000);
  if (diffSec < 45) return "just now";
  const diffMin = Math.round(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m`;
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h`;
  const diffDay = Math.round(diffHr / 24);
  if (diffDay < 7) return `${diffDay}d`;
  const now = new Date();
  if (date.getFullYear() === now.getFullYear()) {
    return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  }
  return date.toLocaleDateString(undefined, { year: "2-digit", month: "short", day: "numeric" });
}
